import React, { useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  TextField,
  Typography,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import CloseIcon from "@material-ui/icons/Close";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import moment from "moment-timezone";

import DashboardWrapper from "../containers/DashboardWrapper";
import { getPlotData, setCurrentPage } from "../actions";
import LocalStorageHelper from "../utils/helper/localstorage_helper";
import Period from "../components/Filter/Period";
import PlotMapboxMap from "../components/Plot/PlotMapboxMap";
import { getAssetTypeColor } from "../constants/plotColors";

const DEFAULT_MAP_CENTER = [139.767125, 35.681236];
const DEFAULT_MAP_ZOOM = 11.7;
const sidePanelWidth = 360;

const TRUST_COLOR = {
  4: "#22c55e",
  3: "#eab308",
  2: "#fb923c",
  1: "#ef4444",
};

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: "calc(100vh - 64px)",
    display: "flex",
    flexDirection: "column",
    background:
      "radial-gradient(circle at top left, rgba(41, 128, 185, 0.12), transparent 30%), linear-gradient(180deg, #f7fafc 0%, #eef4f8 100%)",
  },
  topBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
    background: "rgba(255,255,255,0.85)",
    backdropFilter: "blur(10px)",
  },
  topBarLeft: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    flexWrap: "nowrap",
    minWidth: 0,
  },
  topBarRight: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    marginLeft: "auto",
    minWidth: 0,
  },
  titleBlock: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    marginRight: theme.spacing(1),
    flexShrink: 0,
  },
  title: {
    fontSize: 18,
    fontWeight: 700,
    color: "#0f172a",
  },
  subtitle: {
    fontSize: 12,
    color: "#64748b",
  },
  countText: {
    fontSize: 12,
    color: "#0f766e",
    border: "1px solid #0f766e",
    borderRadius: 999,
    padding: "4px 10px",
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  filter: {
    width: 230,
    background: "#fff",
    flexShrink: 0,
    "& .MuiAutocomplete-inputRoot": {
      flexWrap: "nowrap",
      height: 40,
      overflow: "hidden",
    },
    "& .MuiAutocomplete-input": {
      minWidth: "0 !important",
    },
    "& .MuiAutocomplete-tag": {
      maxWidth: "calc(100% - 36px)",
      overflow: "hidden",
    },
    "& .MuiChip-label": {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
  },
  eventFilter: {
    width: 380,
    background: "#fff",
    flexShrink: 0,
    "& .MuiAutocomplete-inputRoot": {
      flexWrap: "nowrap",
      height: 40,
      overflow: "hidden",
    },
    "& .MuiAutocomplete-input": {
      minWidth: "0 !important",
    },
    "& .MuiAutocomplete-tag": {
      maxWidth: "calc(100% - 36px)",
      overflow: "hidden",
    },
    "& .MuiChip-label": {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
  },
  clearButton: {
    flexShrink: 0,
    whiteSpace: "nowrap",
  },
  content: {
    flex: 1,
    display: "flex",
    overflow: "hidden",
  },
  mapArea: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    position: "relative",
  },
  mapShell: {
    flex: 1,
    position: "relative",
    margin: theme.spacing(2),
    borderRadius: 24,
    overflow: "hidden",
    background:
      "linear-gradient(135deg, rgba(15, 23, 42, 0.92), rgba(30, 41, 59, 0.88))",
    boxShadow: "0 18px 48px rgba(15, 23, 42, 0.16)",
  },
  legend: {
    position: "absolute",
    top: 16,
    right: 16,
    zIndex: 2,
    background: "rgba(255,255,255,0.94)",
    borderRadius: 16,
    padding: theme.spacing(1.5),
    minWidth: 190,
    boxShadow: "0 10px 30px rgba(15, 23, 42, 0.18)",
  },
  legendTitle: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.06em",
    color: "#64748b",
    marginBottom: theme.spacing(1),
    textTransform: "uppercase",
  },
  legendRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    fontSize: 12,
    color: "#334155",
    marginBottom: 6,
  },
  legendRowLeft: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    minWidth: 0,
  },
  legendCheckbox: {
    padding: 0,
    marginRight: 2,
  },
  legendValue: {
    fontVariantNumeric: "tabular-nums",
    color: "#0f172a",
    flexShrink: 0,
  },
  dot: {
    width: 11,
    height: 11,
    borderRadius: "50%",
    flexShrink: 0,
  },
  pin: {
    position: "absolute",
    transform: "translate(-50%, -100%)",
    zIndex: 1,
    border: "none",
    background: "transparent",
    cursor: "pointer",
    color: "#fff",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 2,
  },
  pinBubble: {
    width: 40,
    height: 40,
    borderRadius: "50%",
    display: "grid",
    placeItems: "center",
    fontSize: 18,
    fontWeight: 700,
    boxShadow: "0 8px 18px rgba(0,0,0,0.28)",
    border: "3px solid rgba(255,255,255,0.95)",
  },
  pinStem: {
    width: 0,
    height: 0,
    borderLeft: "7px solid transparent",
    borderRight: "7px solid transparent",
    borderTop: "10px solid rgba(255,255,255,0.95)",
    marginTop: -4,
  },
  thumbWrap: {
    padding: theme.spacing(2, 2, 2),
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: theme.spacing(1.25),
    overflowY: "auto",
  },
  thumbCard: {
    border: "1px solid rgba(148, 163, 184, 0.28)",
    borderRadius: 14,
    overflow: "hidden",
    background: "#fff",
    cursor: "pointer",
    transition: "transform 120ms ease, box-shadow 120ms ease",
    "&:hover": {
      transform: "translateY(-1px)",
      boxShadow: "0 10px 18px rgba(15, 23, 42, 0.12)",
    },
  },
  thumbPreview: {
    height: 92,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 28,
    color: "#fff",
    position: "relative",
    overflow: "hidden",
    background: "linear-gradient(135deg, #0f172a, #334155)",
  },
  thumbImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  thumbOverlay: {
    position: "absolute",
    inset: 0,
    background: "linear-gradient(180deg, rgba(15,23,42,0.08), rgba(15,23,42,0.34))",
  },
  thumbAction: {
    position: "absolute",
    right: 8,
    top: 8,
    width: 26,
    height: 26,
    borderRadius: "50%",
    display: "grid",
    placeItems: "center",
    background: "rgba(255,255,255,0.9)",
    color: "#0f172a",
    boxShadow: "0 6px 16px rgba(15, 23, 42, 0.18)",
  },
  thumbMeta: {
    padding: theme.spacing(1),
    fontSize: 12,
    color: "#0f172a",
  },
  thumbSub: {
    fontSize: 11,
    color: "#64748b",
    marginTop: 2,
  },
  footerNote: {
    position: "absolute",
    left: 16,
    bottom: 16,
    zIndex: 2,
    background: "rgba(15, 23, 42, 0.66)",
    color: "#fff",
    borderRadius: 999,
    padding: "8px 12px",
    fontSize: 12,
  },
  sidePanel: {
    width: 0,
    flexShrink: 0,
    overflow: "hidden",
    borderLeft: "1px solid rgba(15, 23, 42, 0.08)",
    background: "#fff",
    transition: "width 180ms ease",
  },
  sidePanelOpen: {
    width: sidePanelWidth,
  },
  sidePanelInner: {
    width: sidePanelWidth,
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  sidePanelHeader: {
    padding: theme.spacing(2),
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(1),
  },
  sidePanelTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: "#0f172a",
  },
  sidePanelSub: {
    fontSize: 12,
    color: "#64748b",
    marginTop: 4,
  },
  sidePanelClose: {
    border: "none",
    background: "transparent",
    padding: 0,
    lineHeight: 0,
    color: "#9ca3af",
    cursor: "pointer",
    flexShrink: 0,
    marginTop: -2,
    "&:hover": {
      color: "#6b7280",
    },
  },
  sidePanelTitleWrap: {
    minWidth: 0,
    flex: 1,
  },
  loadingBanner: {
    position: "absolute",
    left: 16,
    top: 16,
    zIndex: 2,
    background: "rgba(15, 23, 42, 0.82)",
    color: "#fff",
    borderRadius: 999,
    padding: "8px 12px",
    fontSize: 12,
  },
  emptyOverlay: {
    position: "absolute",
    inset: 0,
    display: "grid",
    placeItems: "center",
    background: "linear-gradient(135deg, rgba(15,23,42,0.82), rgba(30,41,59,0.72))",
    color: "#e2e8f0",
    textAlign: "center",
    padding: 24,
  },
  emptyOverlayInner: {
    maxWidth: 360,
    lineHeight: 1.8,
    fontSize: 13,
  },
}));

const trustLabel = (lv) => `Lv${lv}`;

const Plot = ({
  setCurrentPage,
  getPlotData,
  dateRange,
  minMaxDateRange,
  plotData,
  plotSummary,
  plotLoading,
  plotError,
}) => {
  const classes = useStyles();
  const hasLoadedInitialDataRef = useRef(false);
  const [selectedEvent, setSelectedEvent] = useState([]);
  const [selectedMuni, setSelectedMuni] = useState([]);
  const [selectedAssetTypes, setSelectedAssetTypes] = useState(null);
  const [selectedTrustLevels, setSelectedTrustLevels] = useState(null);
  const [activePlot, setActivePlot] = useState(null);
  const companyName = LocalStorageHelper.getCompany() || "";
  const currentAccessKey = localStorage.getItem("access_key");
  const providerLabel = currentAccessKey === "dea" ? "DEA" : (companyName ? companyName.toUpperCase() : "UNKNOWN");
  const plotMinMaxDateRange = useMemo(() => {
    const minDate = moment.tz("2026-08-01", "YYYY-MM-DD", "Asia/Tokyo").startOf("day").toDate();
    const maxDate = moment().tz("Asia/Tokyo").startOf("day").toDate();
    return [minDate, maxDate];
  }, []);

  useEffect(() => {
    setCurrentPage(window.location.pathname);
  }, [setCurrentPage]);

  useEffect(() => {
    if (hasLoadedInitialDataRef.current) {
      return;
    }
    hasLoadedInitialDataRef.current = true;
    getPlotData({ dateRange });
  }, [dateRange, getPlotData]);

  const allPlots = useMemo(() => plotData || [], [plotData]);
  const availableAssetTypes = useMemo(
    () => Array.from(new Set(allPlots.map((plot) => plot.assetType || "不明"))).sort((a, b) => a.localeCompare(b, "ja")),
    [allPlots]
  );
  const trustLevels = [4, 3, 2, 1];

  useEffect(() => {
    setSelectedAssetTypes(null);
    setSelectedTrustLevels(null);
  }, [plotSummary]);

  const eventOptions = useMemo(() => {
    const options = Array.from(
      new Map(
        allPlots
          .map((plot) => {
            const value = plot.eventId || plot.event;
            return value ? [String(value), { value: String(value), label: plot.event || String(value) }] : null;
          })
          .filter(Boolean)
      ).values()
    );
    options.sort((a, b) => a.label.localeCompare(b.label, "ja"));
    return options;
  }, [allPlots]);

  const plotsForMuniOptions = useMemo(() => {
    if (selectedEvent.length === 0) {
      return allPlots;
    }
    return allPlots.filter((plot) => selectedEvent.includes(String(plot.eventId || plot.event)));
  }, [allPlots, selectedEvent]);

  const muniOptions = useMemo(() => {
    const options = Array.from(
      new Map(
        plotsForMuniOptions
          .map((plot) => {
            const value = plot.municipalityId || plot.municipality;
            return value
              ? [String(value), { value: String(value), label: plot.municipality || String(value) }]
              : null;
          })
          .filter(Boolean)
      ).values()
    );
    options.sort((a, b) => a.label.localeCompare(b.label, "ja"));
    return options;
  }, [plotsForMuniOptions]);

  useEffect(() => {
    const availableValues = new Set(eventOptions.map((option) => option.value));
    setSelectedEvent((current) => {
      const next = current.filter((value) => availableValues.has(value));
      return next.length === current.length ? current : next;
    });
  }, [eventOptions]);

  useEffect(() => {
    const availableValues = new Set(muniOptions.map((option) => option.value));
    setSelectedMuni((current) => {
      const next = current.filter((value) => availableValues.has(value));
      return next.length === current.length ? current : next;
    });
  }, [muniOptions]);

  const selectedEventOptions = useMemo(
    () => eventOptions.filter((option) => selectedEvent.includes(option.value)),
    [eventOptions, selectedEvent]
  );
  const selectedMuniOptions = useMemo(
    () => muniOptions.filter((option) => selectedMuni.includes(option.value)),
    [muniOptions, selectedMuni]
  );

  const isEventSelected = (plot) => {
    const eventValue = plot.eventId || plot.event;
    return selectedEvent.includes(String(eventValue));
  };

  const isMuniSelected = (plot) => {
    const muniValue = plot.municipalityId || plot.municipality;
    return selectedMuni.includes(String(muniValue));
  };

  const filteredPlots = useMemo(() => {
    return allPlots.filter((plot) => {
      if (selectedEvent.length > 0 && !isEventSelected(plot)) {
        return false;
      }
      if (selectedMuni.length > 0 && !isMuniSelected(plot)) {
        return false;
      }
      if (selectedAssetTypes !== null && !selectedAssetTypes.includes(plot.assetType || "不明")) {
        return false;
      }
      if (selectedTrustLevels !== null && !selectedTrustLevels.includes(Number(plot.lv))) {
        return false;
      }
      return true;
    });
  }, [allPlots, selectedAssetTypes, selectedEvent, selectedMuni, selectedTrustLevels]);

  const hasActiveFilters =
    selectedEvent.length > 0 ||
    selectedMuni.length > 0 ||
    selectedAssetTypes !== null ||
    selectedTrustLevels !== null;

  const summaryPlotCount = Number(plotSummary?.totals?.plots || 0);
  const totalPlotCount = !hasActiveFilters && summaryPlotCount > 0 ? summaryPlotCount : allPlots.length;

  const toggleAssetType = (assetType) => {
    setSelectedAssetTypes((current) => {
      const next = new Set(current === null ? availableAssetTypes : current);
      if (next.has(assetType)) {
        next.delete(assetType);
      } else {
        next.add(assetType);
      }
      return next.size === availableAssetTypes.length ? null : Array.from(next);
    });
  };

  const toggleAllAssetTypes = () => {
    setSelectedAssetTypes((current) => {
      if (current === null || current.length === availableAssetTypes.length) {
        return [];
      }
      return null;
    });
  };

  const toggleTrustLevel = (lv) => {
    setSelectedTrustLevels((current) => {
      const next = new Set(current === null ? trustLevels : current);
      if (next.has(lv)) {
        next.delete(lv);
      } else {
        next.add(lv);
      }
      return next.size === trustLevels.length ? null : Array.from(next);
    });
  };

  const toggleAllTrustLevels = () => {
    setSelectedTrustLevels((current) => {
      if (current === null || current.length === trustLevels.length) {
        return [];
      }
      return null;
    });
  };

  useEffect(() => {
    if (activePlot && !filteredPlots.some((plot) => plot.id === activePlot.id)) {
      setActivePlot(null);
    }
  }, [activePlot, filteredPlots]);

  const openViewer = (photoFileName) => {
    if (!photoFileName) {
      return;
    }
    window.open(`/plot-viewer/${encodeURIComponent(photoFileName)}`, "_blank", "noopener,noreferrer");
  };

  const isPanelOpen = Boolean(activePlot);
  const activePlotPhotos = activePlot?.photos || [];

  const trustLegend = useMemo(() => {
    if (!hasActiveFilters && Array.isArray(plotSummary?.trustCounts) && plotSummary.trustCounts.length > 0) {
      const total = plotSummary.trustCounts.reduce((sum, item) => sum + Number(item.count || 0), 0) || 1;
      return [4, 3, 2, 1].map((lv) => {
        const item = plotSummary.trustCounts.find((entry) => Number(entry.lv) === lv);
        const count = Number(item?.count || 0);
        return {
          lv,
          count,
          pct: (count / total) * 100,
        };
      });
    }

    const counts = filteredPlots.reduce(
      (acc, plot) => {
        acc[plot.lv] = (acc[plot.lv] || 0) + 1;
        return acc;
      },
      { 1: 0, 2: 0, 3: 0, 4: 0 }
    );
    const total = filteredPlots.length || 1;
    return [4, 3, 2, 1].map((lv) => ({
      lv,
      count: counts[lv] || 0,
      pct: ((counts[lv] || 0) / total) * 100,
    }));
  }, [filteredPlots, hasActiveFilters, plotSummary]);

  const assetLegend = useMemo(() => {
    if (!hasActiveFilters && Array.isArray(plotSummary?.assetTypeCounts) && plotSummary.assetTypeCounts.length > 0) {
      return plotSummary.assetTypeCounts.map((item) => ({
        assetType: item.assetType || item.label || "不明",
        count: Number(item.count || 0),
        color: getAssetTypeColor(item.assetType || item.label || "不明"),
      }));
    }

    const countsByType = filteredPlots.reduce((acc, plot) => {
      const assetType = plot.assetType || "不明";
      acc[assetType] = (acc[assetType] || 0) + 1;
      return acc;
    }, {});

    return availableAssetTypes.map((assetType) => ({
      assetType,
      count: countsByType[assetType] || 0,
      color: getAssetTypeColor(assetType),
    }));
  }, [availableAssetTypes, filteredPlots, hasActiveFilters, plotSummary]);

  return (
    <DashboardWrapper padding={false}>
      <div className={classes.root}>
        <div className={classes.topBar}>
          <div className={classes.topBarLeft}>
            <div className={classes.titleBlock}>
              <div className={classes.title}>プロット</div>
            </div>
            <Autocomplete
              multiple
              disableCloseOnSelect
              limitTags={1}
              size="small"
              className={classes.eventFilter}
              options={eventOptions}
              value={selectedEventOptions}
              getOptionLabel={(option) => option.label}
              getOptionSelected={(option, value) => option.value === value.value}
              onChange={(_, values) => setSelectedEvent(values.map((option) => option.value))}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="イベント"
                  placeholder={selectedEvent.length === 0 ? "全て" : ""}
                />
              )}
            />
            <Autocomplete
              multiple
              disableCloseOnSelect
              limitTags={1}
              size="small"
              className={classes.filter}
              options={muniOptions}
              value={selectedMuniOptions}
              getOptionLabel={(option) => option.label}
              getOptionSelected={(option, value) => option.value === value.value}
              onChange={(_, values) => setSelectedMuni(values.map((option) => option.value))}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="自治体"
                  placeholder={selectedMuni.length === 0 ? "全て" : ""}
                />
              )}
            />
            <div className={classes.countText}>
              表示 {hasActiveFilters ? filteredPlots.length : totalPlotCount} 件 / 全 {totalPlotCount} 件
            </div>
            {hasActiveFilters && (
              <Button
                className={classes.clearButton}
                onClick={() => {
                  setSelectedEvent([]);
                  setSelectedMuni([]);
                  setSelectedAssetTypes(null);
                  setSelectedTrustLevels(null);
                }}
              >
                クリア
              </Button>
            )}
          </div>
          <div className={classes.topBarRight}>
            <Period
              companyName={companyName}
              plotMode={true}
              dateRange={dateRange}
              minMaxDateRange={plotMinMaxDateRange}
              onApply={(range) => getPlotData({ dateRange: range })}
            />
          </div>
        </div>

        <div className={classes.content}>
          <div className={classes.mapArea}>
            <div className={classes.mapShell}>
              <PlotMapboxMap
                plots={filteredPlots}
                activePlotId={activePlot?.id}
                onSelectPlot={setActivePlot}
                panelOpen={isPanelOpen}
                initialCenter={DEFAULT_MAP_CENTER}
                initialZoom={DEFAULT_MAP_ZOOM}
              />

              {plotLoading && <div className={classes.loadingBanner}>データを読み込み中です。</div>}
              {plotError && !plotLoading && <div className={classes.loadingBanner}>{plotError}</div>}
              {!plotLoading && !plotError && filteredPlots.length === 0 && (
                <div className={classes.emptyOverlay}>
                  <div className={classes.emptyOverlayInner}>
                    条件に一致するプロットがありません。
                    <br />
                    日付・イベント・自治体の条件を見直してください。
                  </div>
                </div>
              )}

              <div className={classes.legend}>
                <div className={classes.legendTitle}>信頼度</div>
                <div className={classes.legendRow}>
                  <div className={classes.legendRowLeft}>
                    <Checkbox
                      className={classes.legendCheckbox}
                      size="small"
                      checked={selectedTrustLevels === null}
                      indeterminate={selectedTrustLevels !== null && selectedTrustLevels.length > 0}
                      onChange={toggleAllTrustLevels}
                      inputProps={{ "aria-label": "信頼度を全選択" }}
                    />
                    <span>全て</span>
                  </div>
                </div>
                {trustLegend.map((item) => (
                  <div className={classes.legendRow} key={item.lv}>
                    <div className={classes.legendRowLeft}>
                      <Checkbox
                        className={classes.legendCheckbox}
                        size="small"
                        checked={selectedTrustLevels === null || selectedTrustLevels.includes(item.lv)}
                        onChange={() => toggleTrustLevel(item.lv)}
                        inputProps={{ "aria-label": `${trustLabel(item.lv)}を選択` }}
                      />
                      <span className={classes.dot} style={{ background: TRUST_COLOR[item.lv] }} />
                      <span>{trustLabel(item.lv)}</span>
                    </div>
                    <span className={classes.legendValue}>
                      {item.count} ({item.pct.toFixed(1)}%)
                    </span>
                  </div>
                ))}

                <Divider style={{ margin: "8px 0" }} />

                <div className={classes.legendTitle}>アセット種別</div>
                <div className={classes.legendRow}>
                  <div className={classes.legendRowLeft}>
                    <Checkbox
                      className={classes.legendCheckbox}
                      size="small"
                      checked={selectedAssetTypes === null}
                      indeterminate={selectedAssetTypes !== null && selectedAssetTypes.length > 0}
                      onChange={toggleAllAssetTypes}
                      inputProps={{ "aria-label": "アセット種別を全選択" }}
                    />
                    <span>全て</span>
                  </div>
                </div>
                {assetLegend.map((item) => (
                  <div className={classes.legendRow} key={item.assetType}>
                    <div className={classes.legendRowLeft}>
                      <Checkbox
                        className={classes.legendCheckbox}
                        size="small"
                        checked={selectedAssetTypes === null || selectedAssetTypes.includes(item.assetType)}
                        onChange={() => toggleAssetType(item.assetType)}
                        inputProps={{ "aria-label": `${item.assetType}を選択` }}
                      />
                      <span className={classes.dot} style={{ background: item.color }} />
                      <span>{item.assetType}</span>
                    </div>
                    <span className={classes.legendValue}>{item.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className={`${classes.sidePanel} ${isPanelOpen ? classes.sidePanelOpen : ""}`}>
            {isPanelOpen && activePlot && (
              <div className={classes.sidePanelInner}>
                <div className={classes.sidePanelHeader}>
                  <div className={classes.sidePanelTitleWrap}>
                    <Typography className={classes.sidePanelTitle}>{activePlot.name}</Typography>
                    <Typography className={classes.sidePanelSub}>
                      {activePlot.id} / {activePlot.municipality} / {activePlot.assetType} / {trustLabel(activePlot.lv)}
                    </Typography>
                  </div>
                  <button
                    type="button"
                    className={classes.sidePanelClose}
                    onClick={() => setActivePlot(null)}
                    aria-label="閉じる"
                  >
                    <CloseIcon />
                  </button>
                </div>
                <Divider />
                <Box className={classes.thumbWrap}>
                  {activePlotPhotos.length > 0 ? (
                    activePlotPhotos.map((photo) => (
                      <div
                        key={photo.id}
                        className={classes.thumbCard}
                        onClick={() => openViewer(photo.fileName)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            openViewer(photo.fileName);
                          }
                        }}
                      >
                        <div
                          className={classes.thumbPreview}
                          style={{
                            backgroundImage: photo.thumbUrl
                              ? `linear-gradient(180deg, rgba(15,23,42,0.08), rgba(15,23,42,0.34)), url(${photo.thumbUrl})`
                              : "linear-gradient(135deg, #0f172a, #334155)",
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                          }}
                        >
                          <div className={classes.thumbOverlay} />
                          <div className={classes.thumbAction}>
                            <OpenInNewIcon style={{ fontSize: 14 }} />
                          </div>
                        </div>
                        <div className={classes.thumbMeta}>
                          <div>{photo.label}</div>
                          <div className={classes.thumbSub}>{photo.capturedAt || " "}</div>
                          <div className={classes.thumbSub}>
                            {photo.fileName || "file_name not found"}
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div style={{ padding: 16, fontSize: 13, color: "#64748b" }}>
                      このアセットにはサムネイルがありません。
                    </div>
                  )}
                </Box>
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardWrapper>
  );
};

Plot.propTypes = {
  setCurrentPage: PropTypes.func.isRequired,
  getPlotData: PropTypes.func.isRequired,
  dateRange: PropTypes.array,
  minMaxDateRange: PropTypes.array,
  plotData: PropTypes.array,
  plotSummary: PropTypes.object,
  plotLoading: PropTypes.bool,
  plotError: PropTypes.string,
};

const mapStateToProps = (state) => ({
  dateRange: state.app.dateRange,
  minMaxDateRange: state.app.minMaxDateRange,
  plotData: state.plot.plotData,
  plotSummary: state.plot.plotSummary,
  plotLoading: state.plot.plotLoading,
  plotError: state.plot.plotError,
});

const mapDispatchToProps = (dispatch) => ({
  setCurrentPage: (param) => dispatch(setCurrentPage(param)),
  getPlotData: (param) => dispatch(getPlotData(param)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Plot);
